/**
 * proposalBody — the Brain-grounded proposal body composer (the weekly-
 * update composer discipline, applied to proposals).
 *
 * `composeProposalBody(account, brainFacts, lineItems)` builds the prose
 * sections of a proposal from REAL data only:
 *
 *   - executiveSummary — from the account's `goals` Brain facts;
 *   - aboutUs          — from `identity` + `positioning` Brain facts;
 *   - scopeOfWork      — derived from the actual line-item descriptions;
 *   - whyUs            — from `positioning` differentiator facts;
 *   - nextSteps        — procedural (no factual claims), personalised with
 *                        the account's recorded POC when one exists.
 *
 * The honesty contract: nothing is invented. A section whose source facts
 * are absent is OMITTED (undefined), so an empty Brain honestly yields just
 * the scope (from real line items) + next steps. Every embedded string is a
 * verbatim Brain fact value or account/line-item field.
 *
 * Pure + DOM-free: records in, sections out. Serialization to/from the
 * document's markdown `body` lives here too so the editor, the preview,
 * and tests all share one canonical format.
 */

import type { Account, BrainFact } from '../db/types';
import type { ProposalLineItem } from '../crm/proposalDoc';

// ---------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------

/** The prose sections of a proposal body. Absent = no source data. */
export interface ProposalBodySections {
  executiveSummary?: string;
  aboutUs?: string;
  /** One scope line per line item, newline-separated. */
  scopeOfWork?: string;
  /** One differentiator per line, newline-separated. */
  whyUs?: string;
  /** Ordered procedural steps, newline-separated. */
  nextSteps?: string;
}

/** Minimal account shape the composer reads (keeps it store-free). */
export type ProposalBodyAccount = Pick<Account, 'name' | 'pocName'> | null;

// ---------------------------------------------------------------------------
// Compose (pure)
// ---------------------------------------------------------------------------

const DIFFERENTIATOR_RE = /differen|advantage|moat|why[\s_-]?us|unique/i;

/**
 * Compose the proposal body sections from the Brain + real records.
 * Sections without source facts are omitted — never padded with filler.
 */
export function composeProposalBody(
  account: ProposalBodyAccount,
  brainFacts: BrainFact[],
  lineItems: ProposalLineItem[],
): ProposalBodySections {
  const bySection = (s: BrainFact['section']) =>
    brainFacts.filter((f) => f.section === s && f.value.trim() !== '');

  const goals = bySection('goals');
  const identity = bySection('identity');
  const positioning = bySection('positioning');
  const differentiators = positioning.filter((f) =>
    DIFFERENTIATOR_RE.test(`${f.field} ${f.label}`),
  );
  const positioningRest = positioning.filter(
    (f) => !differentiators.includes(f),
  );

  const sections: ProposalBodySections = {};

  // Executive summary — only when the Brain holds real goals.
  if (goals.length > 0) {
    const who = account?.name?.trim() || 'the client';
    const goalList = goals.map((f) => `${f.label}: ${f.value}`).join('; ');
    sections.executiveSummary =
      `This engagement is designed around ${who}'s goals on record — ${goalList}. ` +
      'The scope, pricing, and terms below remain open to revision until countersigned.';
  }

  // About us — identity + (non-differentiator) positioning facts, verbatim.
  const aboutFacts = [...identity, ...positioningRest];
  if (aboutFacts.length > 0) {
    sections.aboutUs = aboutFacts
      .map((f) => `${f.label}: ${f.value}`)
      .join('\n');
  }

  // Scope of work — one line per REAL line item, mirroring the pricing table.
  if (lineItems.length > 0) {
    sections.scopeOfWork = lineItems
      .map((item) => {
        const unit = item.unit ? ` ${item.unit}` : '';
        const cadence = item.cadence === 'recurring' ? 'recurring' : 'one-time';
        return `${item.description} — ${item.qty}${unit}, ${cadence}.`;
      })
      .join('\n');
  }

  // Why us — Brain differentiators only. No differentiators, no section.
  if (differentiators.length > 0) {
    sections.whyUs = differentiators.map((f) => f.value).join('\n');
  }

  // Next steps — procedural, no factual claims; POC name comes from the
  // account record when present.
  const kickoff = account?.pocName?.trim()
    ? `We'll schedule a kickoff with ${account.pocName.trim()} once signed.`
    : `We'll schedule a kickoff once signed.`;
  sections.nextSteps = [
    'Review the scope and pricing in this document.',
    'Reply with any revisions — scope and terms stay open until countersigned.',
    kickoff,
  ].join('\n');

  return sections;
}

// ---------------------------------------------------------------------------
// Markdown serialization (the document's `body` format)
// ---------------------------------------------------------------------------

/** Canonical heading per section, in render order. */
const SECTION_HEADINGS: Array<[keyof ProposalBodySections, string]> = [
  ['executiveSummary', 'Executive summary'],
  ['aboutUs', 'About us'],
  ['scopeOfWork', 'Scope of work'],
  ['whyUs', 'Why us'],
  ['nextSteps', 'Next steps'],
];

/** Serialize composed sections to the doc's markdown body. */
export function serializeProposalBody(sections: ProposalBodySections): string {
  return SECTION_HEADINGS.filter(([key]) => sections[key]?.trim())
    .map(([key, heading]) => `## ${heading}\n\n${sections[key]!.trim()}`)
    .join('\n\n');
}

/**
 * Parse a markdown body back into the known sections. Unknown headings are
 * ignored; hand-edited text under a canonical heading is preserved verbatim.
 * Returns {} when the body carries none of the canonical headings.
 */
export function parseProposalBody(body: string): ProposalBodySections {
  const sections: ProposalBodySections = {};
  const headingToKey = new Map(
    SECTION_HEADINGS.map(([key, heading]) => [heading.toLowerCase(), key]),
  );
  let currentKey: keyof ProposalBodySections | null = null;
  let buf: string[] = [];
  const flush = () => {
    if (currentKey) {
      const text = buf.join('\n').trim();
      if (text) sections[currentKey] = text;
    }
    buf = [];
  };
  for (const line of body.split('\n')) {
    const m = /^##\s+(.+?)\s*$/.exec(line);
    if (m) {
      flush();
      currentKey = headingToKey.get(m[1].toLowerCase()) ?? null;
      continue;
    }
    buf.push(line);
  }
  flush();
  return sections;
}

/** True when any section is present. */
export function hasBodySections(sections: ProposalBodySections): boolean {
  return SECTION_HEADINGS.some(([key]) => Boolean(sections[key]?.trim()));
}

// ---------------------------------------------------------------------------
// Overwrite guard
// ---------------------------------------------------------------------------

/**
 * Should "Generate body" ask before replacing the current body?
 * Yes when the body is non-empty AND is not verbatim the last generated
 * composition — i.e. it carries hand-written or hand-edited content.
 */
export function needsOverwriteConfirm(
  currentBody: string,
  lastGenerated: string | null,
): boolean {
  const cur = currentBody.trim();
  if (!cur) return false;
  return cur !== (lastGenerated ?? '').trim();
}
